import React, { useMemo } from "react";
import "./PrivacyPolicy.css";
import { useLocalContext } from "../../context/LocalContext";

const PrivacyPolicy = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "virtuedigihost.com",
      email: webinfo.email || "contact@virtuedigihost.com",
      phone: webinfo.phone || "+1 (000) 000-0000",
      phonecall: webinfo.phonecall || "+10000000000",
      address: webinfo.address || "United States",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="pp-about">
      <div className="pp-container">
        <header className="pp-header">
          <span className="pp-badge">Privacy Policy — United States</span>
          <h1>Privacy Policy</h1>
          <p className="pp-intro">
            <strong>{brand.name}</strong> (“we,” “us,” or “our”) describes in this
            Privacy Policy how we collect, use, disclose, and protect personal
            information when you visit our U.S.-directed website, purchase or use our
            hosting and related services, or communicate with us. This policy is
            intended to comply with applicable U.S. federal and state privacy laws,
            including where noted for California residents.
          </p>

          <div className="pp-meta">
            <div>
              <span>Business</span>
              <strong>{brand.name}</strong>
            </div>
            <div>
              <span>Last updated</span>
              <strong>{effectiveDate}</strong>
            </div>
          </div>
        </header>

        <article className="pp-content">
          <section>
            <h2>1. Scope & U.S. audience</h2>
            <p>
              This policy applies to personal information we process in connection with
              our services offered to individuals and businesses in the United States.
              If you access our services from outside the U.S., your information may be
              processed in the U.S. where our servers and vendors are located. By using
              our services, you understand that your information may be transferred to
              and processed in the United States, which may have different data
              protection rules than your country.
            </p>
          </section>

          <section>
            <h2>2. Information we collect</h2>
            <p>Depending on how you interact with us, we may collect:</p>
            <ul>
              <li>
                <strong>Identifiers:</strong> name, email address, postal address,
                phone number, account username or customer ID.
              </li>
              <li>
                <strong>Commercial information:</strong> services purchased, billing
                and payment-related records (payment processing is handled by our
                payment vendors; we do not store full payment card numbers on our
                servers where avoidable).
              </li>
              <li>
                <strong>Internet or network activity:</strong> IP address, browser
                type, device identifiers, pages viewed, and approximate location derived
                from IP.
              </li>
              <li>
                <strong>Customer content:</strong> files, configurations, domains, and
                other content you upload or host with us as part of hosting services.
              </li>
              <li>
                <strong>Communications:</strong> messages you send to support, sales,
                or abuse contacts.
              </li>
            </ul>
            <p>
              We collect personal information directly from you, automatically through
              cookies and similar technologies, and in some cases from service
              providers or fraud-prevention partners as permitted by law.
            </p>
          </section>

          <section>
            <h2>3. How we use information (business purposes)</h2>
            <p>We use personal information to:</p>
            <ul>
              <li>Provide, operate, secure, and improve our hosting and related services.</li>
              <li>Process orders, authenticate accounts, and communicate with you.</li>
              <li>Detect, prevent, and respond to fraud, abuse, and security incidents.</li>
              <li>Comply with law, enforce our Terms of Service, and defend legal claims.</li>
              <li>Analyze usage in aggregate or de-identified form to improve our website.</li>
              <li>
                Send transactional messages and, where permitted, marketing communications
                (you may opt out of marketing as described below).
              </li>
            </ul>
            <p>
              We do not sell your personal information for money. We do not use or
              disclose sensitive personal information for purposes other than those
              permitted under applicable California law where the California Consumer
              Privacy Act (CCPA), as amended by the California Privacy Rights Act
              (CPRA), applies.
            </p>
          </section>

          <section>
            <h2>4. Cookies & similar technologies</h2>
            <p>
              We and our analytics or advertising partners may use cookies, pixels,
              and similar technologies to remember preferences, measure traffic, and
              understand how our site is used. You can control cookies through your
              browser settings. Disabling cookies may affect site functionality.
            </p>
          </section>

          <section>
            <h2>5. Disclosure of information</h2>
            <p>We may disclose personal information:</p>
            <ul>
              <li>
                To <strong>service providers</strong> who assist us (e.g., hosting
                infrastructure, payment processing, email delivery, customer support)
                under contracts that limit their use of your data.
              </li>
              <li>
                For <strong>legal reasons</strong>, including to comply with a lawful
                request from public authorities, subpoena, court order, or applicable
                U.S. law.
              </li>
              <li>
                In connection with a <strong>business transaction</strong> such as a
                merger, acquisition, or asset sale, subject to appropriate confidentiality
                and notice requirements.
              </li>
              <li>
                With your <strong>direction or consent</strong>.
              </li>
            </ul>
          </section>

          <section>
            <h2>6. Your U.S. privacy rights</h2>
            <p>
              Depending on your state of residence, you may have rights under
              applicable state privacy laws (including California, Colorado, Virginia,
              Connecticut, Utah, and others) such as:
            </p>
            <ul>
              <li>
                The right to <strong>know</strong> what personal information we collect,
                use, and disclose.
              </li>
              <li>
                The right to <strong>delete</strong> personal information we hold,
                subject to exceptions.
              </li>
              <li>
                The right to <strong>correct</strong> inaccurate personal information.
              </li>
              <li>
                The right to <strong>opt out</strong> of certain processing, including
                “sales” or “sharing” for cross-context behavioral advertising where
                those terms apply (we do not sell personal information for monetary
                consideration; see our practices above).
              </li>
              <li>
                The right to <strong>appeal</strong> a decision we make regarding your
                request, where required by law.
              </li>
              <li>
                The right to <strong>non-discrimination</strong> for exercising privacy
                rights.
              </li>
            </ul>
            <p>
              <strong>California residents:</strong> You may exercise CCPA/CPRA rights
              by emailing{" "}
              <a href={`mailto:${brand.email}`}>{brand.email}</a> with “California
              Privacy Request” in the subject line, or by calling{" "}
              <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>. We will verify your
              request in accordance with applicable law. You may designate an authorized
              agent to submit a request on your behalf with written permission.
            </p>
            <p>
              <strong>California “Shine the Light” (Cal. Civ. Code § 1798.83):</strong>{" "}
              California residents may request information about disclosure of
              categories of personal information to third parties for their direct
              marketing purposes once per calendar year. We do not share personal
              information with third parties for their direct marketing purposes as
              defined in that statute.
            </p>
          </section>

          <section>
            <h2>7. Children’s privacy (COPPA)</h2>
            <p>
              Our services are not directed to children under 13 years of age in the
              United States, and we do not knowingly collect personal information from
              children under 13 in violation of the Children’s Online Privacy Protection
              Act (COPPA). If you believe we have collected information from a child
              under 13, please contact us and we will take appropriate steps to delete
              it.
            </p>
          </section>

          <section>
            <h2>8. Security</h2>
            <p>
              We implement administrative, technical, and physical safeguards designed
              to protect personal information appropriate to the nature of the data and
              our services. No method of transmission over the Internet or electronic
              storage is 100% secure; we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>9. Retention</h2>
            <p>
              We retain personal information for as long as needed to provide services,
              fulfill the purposes described in this policy, comply with legal
              obligations (including tax and accounting rules), resolve disputes, and
              enforce our agreements. Retention periods vary by data type and legal
              requirements.
            </p>
          </section>

          <section>
            <h2>10. Marketing & communications</h2>
            <p>
              We may send you service-related emails (e.g., invoices, security notices)
              which are necessary to perform our contract with you. Where we send
              promotional emails under U.S. law (including CAN-SPAM), you may
              unsubscribe using the link in those messages or by contacting{" "}
              <a href={`mailto:${brand.email}`}>{brand.email}</a>.
            </p>
          </section>

          <section>
            <h2>11. Changes to this Privacy Policy</h2>
            <p>
              We may update this Privacy Policy to reflect changes in our practices or
              applicable U.S. law. We will post the revised policy and update the “last
              updated” date. Where required by law, we will provide additional notice.
            </p>
          </section>

          <section>
            <h2>12. Contact us</h2>
            <p>
              For privacy questions or to exercise your rights under U.S. state and
              federal law, contact:
            </p>
            <ul className="pp-contact">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
              </li>
              <li>
                <strong>Postal address:</strong> {brand.address}
              </li>
            </ul>
            <p className="pp-legal-note">
              Nothing in this Privacy Policy is intended to create rights for third
              parties or to waive any rights or defenses available to us under
              applicable state or federal law.
            </p>
          </section>
        </article>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
